import { insertItems as indexInsert, deleteItem as indexDelete, editItem as indexEdit } from './pager_index.js';

/**
 * A sorted index over a keyed list, paged for display.
 *
 * `map` holds 'sortValue<U+10FFFF>id' strings in sort order; `list` holds the
 * items by id. Everything a page needs is a slice of `map` resolved through
 * `list`.
 *
 * These operations used to run in a Web Worker, one worker per pager. Placing an
 * entry is a binary search, which is O(log n); handing the index to a worker and
 * receiving it back is a structured clone of the whole index, twice, which is
 * O(n) with a very large constant. Inserting n files into one folder therefore
 * cloned 1 + 2 + ... + n items on the MAIN thread: measured against the real
 * worker, 3000 files moved 803 MB, and the traffic grew exactly 4x per doubling.
 * A bulk upload into a large folder is where that lands, and it is the browser
 * process, not the tab, that gets the bill for it. The algorithm now runs here,
 * out of the same module the worker uses, so the two cannot disagree.
 */
export default class Pager {
    sortBy: string;
    id: string;
    order: 'asc' | 'desc' = 'asc';
    map: any[] = []; // [uniqueId, uniqueId, uniqueId, ...]
    list: { [uniqueId: string]: any } = {};
    resultsPerPage = 25;
    /** Kept for callers that constructed a Pager with one. Unused, see above. */
    worker: any;
    /** Tail of this pager's request chain. See `queued`. */
    private pending: Promise<any> = Promise.resolve();

    constructor(
        worker: Worker | null,
        options: {
            id: string; // unique id of the data
            sortBy?: string; // sort by which key
            order?: 'asc' | 'desc'; // sort order
            resultsPerPage?: number; // how many items per page
        }
    ) {
        let { id, sortBy, order = 'asc', resultsPerPage = 25 } = options;
        if (!id || typeof id !== 'string') {
            throw 'id is required';
        }
        this.worker = worker;
        this.id = id;
        this.sortBy = sortBy || id;
        this.order = order;
        this.resultsPerPage = resultsPerPage;
    }

    /**
     * One mutation at a time, in call order.
     *
     * These methods are synchronous now, so this no longer has to guard a shared
     * reply slot the way it did when a worker answered them (two overlapping
     * calls resolved each other's promise, and the loser never settled at all,
     * which parked an upload worker for good). It stays because the API is
     * asynchronous and callers are entitled to the order they issued.
     */
    private queued<T>(run: () => T): Promise<T> {
        const next = this.pending.then(run, run);
        // A failure must not wedge the chain for every later call.
        this.pending = next.then(() => { }, () => { });
        return next;
    }

    getPage(page: number): {
        list: Object[];
        maxPage: number;
    } {
        let startIndex = (page - 1) * this.resultsPerPage;
        let result = this.map.slice(startIndex, startIndex + this.resultsPerPage);

        return {
            list: result.map((target: string) => this.list[target.split('\u{10FFFF}')[1]]),
            maxPage: Math.ceil(this.map.length / this.resultsPerPage)
        };
    }

    /**
     * Everything the pager holds, in its sort order, for a table that shows every
     * row it has fetched and grows under a "Load more" button.
     */
    getAll(): Object[] {
        return this.map.map((target: string) => this.list[target.split('\u{10FFFF}')[1]]);
    }

    insertItems(
        items: { [key: string]: any }[],
        options?: {
            withinRange: boolean;
        }
    ): Promise<"Insert Successful"> {
        let { withinRange = false } = options || {};
        if (!Array.isArray(items)) {
            items = [items]
        }

        // Unchanged in ORDER of effect: the local list gains the items in the same
        // turn the call is made, so a caller that does not await still sees them.
        for (let item of items) {
            this.list[item[this.id]] = item;
        }

        return this.queued(() => {
            this.map = indexInsert({
                items,
                sortBy: this.sortBy,
                order: this.order,
                id: this.id,
                map: this.map,
                withinRange,
            });
            return "Insert Successful" as const;
        });
    }

    editItem(
        item: { [key: string]: any },
        options?: {
            withinRange: boolean;
        }
    ): Promise<"Edit Saved"> {
        let { withinRange = false } = options || {};
        if (Array.isArray(item)) {
            throw new Error('item should be an object');
        }

        return this.queued(() => {
            // Index first, list second. The edit decides whether an entry has to
            // MOVE by comparing the item's stored index value with the new one, so
            // merging into the list first would make those two the same value and
            // leave the stale entry in the map. This is the order the worker
            // protocol produced, where the merge happened in the reply handler.
            this.map = indexEdit({
                items: [item],
                list: this.list,
                sortBy: this.sortBy,
                order: this.order,
                id: this.id,
                map: this.map,
                withinRange,
            });

            if (this.list?.[item[this.id]]) {
                Object.assign(this.list[item[this.id]], item);
            }
            else {
                this.list[item[this.id]] = item;
            }
            return "Edit Saved" as const;
        });
    }

    deleteItem(id: string): Promise<"Item Deleted"> {
        // Nothing to delete is not an error. The index algorithm reads
        // `list[items[0][id]]` immediately, so an unknown id would throw on
        // `undefined[id]` before it could conclude there was nothing to do.
        if (this.list?.[id] === undefined) {
            return Promise.resolve("Item Deleted" as const);
        }

        return this.queued(() => {
            this.map = indexDelete({
                items: [this.list[id]],
                list: this.list,
                sortBy: this.sortBy,
                order: this.order,
                id: this.id,
                map: this.map,
            });
            delete this.list[id];
            return "Item Deleted" as const;
        });
    }

    resetIndex(option: {
        sortBy?: string; // sort by which key
        order?: 'asc' | 'desc'; // sort order
        resultsPerPage?: number; // how many items per page
    }): Promise<"Reset Successful"> {
        let { sortBy, order, resultsPerPage } = option;

        if (!sortBy && !order && !resultsPerPage) {
            return Promise.resolve("Reset Successful" as const);
        }

        this.sortBy = sortBy || this.sortBy;
        this.order = order || this.order;
        this.resultsPerPage = resultsPerPage || this.resultsPerPage;

        if (sortBy === this.sortBy && order === this.order && resultsPerPage === this.resultsPerPage) {
            return Promise.resolve("Reset Successful" as const);
        }

        return this.queued(() => {
            this.map = indexInsert({
                items: Object.keys(this.list).map((key) => this.list[key]),
                sortBy: sortBy as string,
                order: order as 'asc' | 'desc',
                id: this.id,
                map: [],
                withinRange: false,
            });
            return "Reset Successful" as const;
        });
    }

    ////////////////////////////////////////////////////////////////
    static async init(option: {
        id: string; // unique id of the data
        sortBy?: string; // sort by which key
        order?: 'asc' | 'desc'; // sort order
        resultsPerPage?: number; // how many items per page
    }): Promise<Pager> {
        // No worker is spawned any more. One was created per pager and none was
        // ever terminated, so browsing n directories left n live threads behind
        // for the life of the tab, on top of the clone traffic above. `init` stays
        // async because every caller awaits it.
        return new Pager(null, option);
    }
}
